<template>
    <div v-if="slides.length > 0" class="campaign-banner-slider">
        <div class="campaign-banner-slider__frame grey-container">
            <transition name="campaign-fade" mode="out-in">
                <component
                    :is="activeSlide.linkUrl ? 'a' : 'div'"
                    :key="slideKey"
                    class="campaign-slide"
                    :class="isVariantOne ? 'campaign-slide--v1' : 'campaign-slide--v2'"
                    :href="activeSlide.linkUrl || null"
                    :target="activeSlide.linkUrl ? '_blank' : null"
                    :rel="activeSlide.linkUrl ? 'noopener noreferrer' : null"
                >
                    <template v-if="isVariantOne">
                        <div
                            class="campaign-slide__v1-panel"
                            :style="getVariantOnePanelStyle(activeSlide.asset)"
                        >
                            <div
                                class="campaign-slide__text-wrapper"
                                :style="getTextWrapperStyle(activeSlide.asset)"
                            >
                                <div
                                    class="campaign-slide__text-box campaign-slide__text-box--v1"
                                    :style="getTextBoxStyle(activeSlide.asset)"
                                >
                                    <h2
                                        v-if="activeSlide.title"
                                        class="campaign-slide__title"
                                        :style="getTitleStyle(activeSlide.asset)"
                                    >
                                        {{ activeSlide.title }}
                                    </h2>
                                    <p
                                        v-if="activeSlide.subtitle"
                                        class="campaign-slide__subtitle"
                                        :style="getSubtitleStyle(activeSlide.asset)"
                                    >
                                        {{ activeSlide.subtitle }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div
                            class="campaign-slide__v1-image-container"
                            :style="{ backgroundColor: activeSlide.asset.bgColor || 'transparent' }"
                        >
                            <img
                                v-if="activeSlideImage"
                                class="campaign-slide__v1-square"
                                :src="activeSlideImage"
                                :alt="activeSlide.title || activeSlide.campaignTitle || 'Aktionsbanner'"
                            >
                        </div>
                    </template>
                    <template v-else>
                        <img
                            v-if="activeSlideImage"
                            class="campaign-slide__image"
                            :src="activeSlideImage"
                            :alt="activeSlide.title || activeSlide.campaignTitle || 'Aktionsbanner'"
                        >
                        <div
                            v-else
                            class="campaign-slide__image campaign-slide__image--fallback"
                            :style="getFallbackImageStyle(activeSlide.asset)"
                        />
                        <div
                            class="campaign-slide__overlay"
                            :style="getOverlayContainerStyle(activeSlide.asset)"
                        >
                            <div
                                class="campaign-slide__text-wrapper"
                                :style="getTextWrapperStyle(activeSlide.asset)"
                            >
                                <div
                                    class="campaign-slide__text-box"
                                    :style="getTextBoxStyle(activeSlide.asset)"
                                >
                                    <h2
                                        v-if="activeSlide.title"
                                        class="campaign-slide__title"
                                        :style="getTitleStyle(activeSlide.asset)"
                                    >
                                        {{ activeSlide.title }}
                                    </h2>
                                    <p
                                        v-if="activeSlide.subtitle"
                                        class="campaign-slide__subtitle"
                                        :style="getSubtitleStyle(activeSlide.asset)"
                                    >
                                        {{ activeSlide.subtitle }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </component>
            </transition>
        </div>

        <div v-if="slides.length > 1" class="campaign-slide__indicators-wrap">
            <div class="campaign-slide__indicators">
                <button
                    v-for="(slide, index) in slides"
                    :key="slide.id + '-' + index"
                    class="campaign-slide__indicator"
                    :class="{ 'is-active': index === activeIndex }"
                    @click="setActiveSlide(index)"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CampaignBannerSlider',
    props: {
        campaigns: {
            type: Array,
            default() {
                return []
            }
        }
    },
    data() {
        return {
            activeIndex: 0,
            rotationTimer: null,
            viewport: 'desktop'
        }
    },
    computed: {
        slides() {
            const slides = []
            for (const campaign of this.campaigns) {
                if (!this.isCampaignActive(campaign)) {
                    continue
                }

                if (!campaign || !Array.isArray(campaign.assets) || campaign.assets.length === 0) {
                    continue
                }

                const asset = campaign.assets[0]
                if (!asset || typeof asset !== 'object') {
                    continue
                }

                if (!this.hasSlideContent(asset)) {
                    continue
                }

                slides.push({
                    id: campaign.id,
                    campaignTitle: campaign.title || null,
                    title: asset.title || null,
                    subtitle: asset.subtitle || null,
                    linkUrl: asset.linkUrl || null,
                    asset
                })
            }

            return slides
        },
        activeSlide() {
            return this.slides[this.activeIndex] || null
        },
        activeSlideImage() {
            if (!this.activeSlide) {
                return ''
            }

            return this.getSlideImage(this.activeSlide.asset)
        },
        isVariantOne() {
            if (!this.activeSlide) {
                return false
            }

            return this.activeSlide.asset.displayVariant === 'variant_1'
        },
        slideKey() {
            if (!this.activeSlide) {
                return 'empty-slide'
            }

            const variant = this.activeSlide.asset.displayVariant || 'variant_2'
            return this.activeSlide.id + '-' + this.activeIndex + '-' + variant
        }
    },
    watch: {
        slides() {
            if (this.activeIndex >= this.slides.length) {
                this.activeIndex = 0
            }

            this.restartRotation()
        }
    },
    mounted() {
        this.updateViewport()
        window.addEventListener('resize', this.updateViewport)
        this.restartRotation()
    },
    beforeUnmount() {
        this.stopRotation()
        window.removeEventListener('resize', this.updateViewport)
    },
    methods: {
        isCampaignActive(campaign) {
            if (!campaign || typeof campaign !== 'object') {
                return false
            }

            const now = Date.now()
            const windows = [
                [campaign.displayFrom, campaign.displayUntil],
                [campaign.validFrom, campaign.validUntil]
            ]

            return windows.every(([from, until]) => {
                const fromTimestamp = Date.parse(from)
                if (!Number.isNaN(fromTimestamp) && now < fromTimestamp) {
                    return false
                }

                const untilTimestamp = Date.parse(until)
                if (!Number.isNaN(untilTimestamp) && now > untilTimestamp) {
                    return false
                }

                return true
            })
        },
        updateViewport() {
            if (window.innerWidth >= 1024) {
                this.viewport = 'desktop'
                return
            }

            if (window.innerWidth >= 769) {
                this.viewport = 'tablet'
                return
            }

            this.viewport = 'mobile'
        },
        restartRotation() {
            this.stopRotation()

            if (this.slides.length <= 1) {
                return
            }

            this.rotationTimer = setInterval(() => {
                this.activeIndex = (this.activeIndex + 1) % this.slides.length
            }, 10000)
        },
        stopRotation() {
            if (this.rotationTimer === null) {
                return
            }

            clearInterval(this.rotationTimer)
            this.rotationTimer = null
        },
        setActiveSlide(index) {
            this.activeIndex = index
            this.restartRotation()
        },
        hasSlideContent(asset) {
            if (!asset || typeof asset !== 'object') {
                return false
            }

            if (this.getSlideImage(asset) !== '') {
                return true
            }

            return Boolean(asset.title || asset.subtitle || asset.bgColor)
        },
        getViewportOrder() {
            if (this.viewport === 'tablet') {
                return ['tablet', 'mobile', 'desktop']
            }

            if (this.viewport === 'mobile') {
                return ['mobile', 'tablet', 'desktop']
            }

            return ['desktop', 'tablet', 'mobile']
        },
        getResponsiveValueByViewport(valuesByViewport) {
            if (
                !valuesByViewport
                || typeof valuesByViewport !== 'object'
                || Array.isArray(valuesByViewport)
            ) {
                return null
            }

            const viewportOrder = this.getViewportOrder()
            const viewportAliases = {
                desktop: ['desktop', 'split', 'desktopLeft'],
                tablet: ['tablet', 'landscape', 'tabletBelowLogin'],
                mobile: ['mobile', 'portrait', 'mobileAboveLogin']
            }

            for (const viewport of viewportOrder) {
                const aliases = viewportAliases[viewport] || [viewport]

                for (const key of aliases) {
                    const value = valuesByViewport[key]
                    if (value !== undefined && value !== null && value !== '') {
                        return value
                    }
                }
            }

            return null
        },
        getLegacyResponsiveStringValue(rawValue, allowedValues) {
            if (typeof rawValue !== 'string') {
                return null
            }

            const normalized = rawValue.trim()
            if (allowedValues.indexOf(normalized) !== -1) {
                return normalized
            }

            if (normalized.startsWith('{') && normalized.endsWith('}')) {
                try {
                    const parsed = JSON.parse(normalized)
                    const viewportValue = this.getResponsiveValueByViewport(parsed)
                    if (
                        typeof viewportValue === 'string'
                        && allowedValues.indexOf(viewportValue) !== -1
                    ) {
                        return viewportValue
                    }
                } catch (_) {
                    // Ignore invalid legacy JSON and continue with other fallbacks.
                }
            }

            if (normalized.includes('|')) {
                const values = normalized.split('|').map(value => value.trim())
                if (this.viewport === 'tablet') {
                    const tabletValue = values[1] || values[0]
                    if (allowedValues.indexOf(tabletValue) !== -1) {
                        return tabletValue
                    }
                } else if (this.viewport === 'mobile') {
                    const mobileValue = values[2] || values[0]
                    if (allowedValues.indexOf(mobileValue) !== -1) {
                        return mobileValue
                    }
                } else {
                    const desktopValue = values[0]
                    if (allowedValues.indexOf(desktopValue) !== -1) {
                        return desktopValue
                    }
                }
            }

            return null
        },
        getImageUrlForViewport(asset, viewport) {
            if (!asset || typeof asset !== 'object') {
                return ''
            }

            const imageFromObject = asset.images?.[viewport]
            if (typeof imageFromObject === 'string' && imageFromObject !== '') {
                return imageFromObject
            }

            const legacyFieldMap = {
                desktop: 'imageDesktopUrl',
                tablet: 'imageTabletUrl',
                mobile: 'imageMobileUrl'
            }
            const legacyImageUrl = asset[legacyFieldMap[viewport]]
            if (typeof legacyImageUrl === 'string' && legacyImageUrl !== '') {
                return legacyImageUrl
            }

            return ''
        },
        getSlideImage(asset) {
            const viewportOrder = this.getViewportOrder()
            const candidates = viewportOrder.map((viewport) => this.getImageUrlForViewport(asset, viewport))

            const firstAvailableImage = candidates.find((imageUrl) => imageUrl !== '')
            return firstAvailableImage || ''
        },
        getTextPosition(asset) {
            const defaultPosition = 'middle-center'
            const allowed = [
                'top-left',
                'top-center',
                'top-right',
                'middle-left',
                'middle-center',
                'middle-right',
                'bottom-left',
                'bottom-center',
                'bottom-right'
            ]
            const textPosition = asset.textPosition

            if (textPosition && typeof textPosition === 'object' && !Array.isArray(textPosition)) {
                const viewportPosition = this.getResponsiveValueByViewport(textPosition)

                if (typeof viewportPosition === 'string' && allowed.indexOf(viewportPosition) !== -1) {
                    return viewportPosition
                }
            }

            const parsedLegacyPosition = this.getLegacyResponsiveStringValue(textPosition, allowed)
            if (parsedLegacyPosition) {
                return parsedLegacyPosition
            }

            const legacyFieldMap = {
                desktop: 'textPositionDesktop',
                tablet: 'textPositionTablet',
                mobile: 'textPositionMobile'
            }
            const viewportOrder = this.getViewportOrder()
            for (const viewport of viewportOrder) {
                const legacyValue = this.getLegacyResponsiveStringValue(
                    asset && asset[legacyFieldMap[viewport]],
                    allowed
                )
                if (legacyValue) {
                    return legacyValue
                }
            }

            return defaultPosition
        },
        getTextAlignment(asset) {
            const allowed = ['left', 'center', 'right']
            const textAlignment = asset && asset.textAlignment

            if (textAlignment && typeof textAlignment === 'object' && !Array.isArray(textAlignment)) {
                const viewportAlignment = this.getResponsiveValueByViewport(textAlignment)
                if (typeof viewportAlignment === 'string' && allowed.indexOf(viewportAlignment) !== -1) {
                    return viewportAlignment
                }
            }

            const parsedLegacyAlignment = this.getLegacyResponsiveStringValue(textAlignment, allowed)
            if (parsedLegacyAlignment) {
                return parsedLegacyAlignment
            }

            const legacyFieldMap = {
                desktop: 'textAlignmentDesktop',
                tablet: 'textAlignmentTablet',
                mobile: 'textAlignmentMobile'
            }
            const viewportOrder = this.getViewportOrder()
            for (const viewport of viewportOrder) {
                const legacyValue = this.getLegacyResponsiveStringValue(
                    asset && asset[legacyFieldMap[viewport]],
                    allowed
                )
                if (legacyValue) {
                    return legacyValue
                }
            }

            return 'left'
        },
        getTextWrapperStyle(asset) {
            const position = this.getTextPosition(asset)
            const [, horizontal] = position.split('-')
            const textAlignment = this.getTextAlignment(asset)

            const textAlignMap = {
                left: 'left',
                center: 'center',
                right: 'right'
            }

            return {
                alignItems: horizontal === 'right'
                    ? 'flex-end'
                    : horizontal === 'left'
                        ? 'flex-start'
                        : 'center',
                textAlign: textAlignMap[textAlignment] || 'center'
            }
        },
        getTextBoxStyle(asset) {
            const position = this.getTextPosition(asset)
            const [vertical, horizontal] = position.split('-')
            const alignSelfMap = {
                left: 'flex-start',
                center: 'center',
                right: 'flex-end'
            }

            const style = {
                alignSelf: alignSelfMap[horizontal] || 'center',
                marginTop: '0',
                marginBottom: '0'
            }

            if (vertical === 'top') {
                style.marginBottom = 'auto'
                return style
            }

            if (vertical === 'bottom') {
                style.marginTop = 'auto'
                return style
            }

            style.marginTop = 'auto'
            style.marginBottom = 'auto'
            return style
        },
        getOverlayContainerStyle(asset) {
            const hasImage = this.getSlideImage(asset) !== ''
            const backgroundColor = hasImage ? 'transparent' : (asset.bgColor || 'transparent')
            const textColor = this.getTextColor(asset)

            return {
                backgroundColor,
                color: textColor
            }
        },
        getVariantOnePanelStyle(asset) {
            return {
                backgroundColor: asset.bgColor || 'transparent',
                color: this.getTextColor(asset)
            }
        },
        getFallbackImageStyle(asset) {
            return {
                backgroundColor: asset.bgColor || 'transparent'
            }
        },
        getFontSizeForViewport(fontSize, defaultSize) {
            if (fontSize && typeof fontSize === 'object' && !Array.isArray(fontSize)) {
                const viewportSize = this.getResponsiveValueByViewport(fontSize)
                if (Number(viewportSize) > 0) {
                    return Number(viewportSize)
                }
            }

            if (Number(fontSize) > 0) {
                return Number(fontSize)
            }

            return defaultSize
        },
        getTextColor(asset) {
            if (!asset || typeof asset !== 'object') {
                return '#ffffff'
            }

            if (typeof asset.textColor === 'string' && asset.textColor !== '') {
                return asset.textColor
            }

            const responsiveTextColor = this.getResponsiveValueByViewport(asset.textColor)
            if (typeof responsiveTextColor === 'string' && responsiveTextColor !== '') {
                return responsiveTextColor
            }

            return '#ffffff'
        },
        getFontFamily(fontName) {
            const normalizedFontName = typeof fontName === 'string'
                ? fontName.trim().toLowerCase()
                : ''

            if (normalizedFontName === 'oswald') {
                return "'Oswald', 'Source Sans Pro', sans-serif"
            }

            if (normalizedFontName === 'lato') {
                return "'Lato', 'Source Sans Pro', sans-serif"
            }

            return "'Source Sans Pro', sans-serif"
        },
        getLineHeight(lineHeight, defaultLineHeight) {
            if (Number(lineHeight) > 0) {
                return Number(lineHeight) / 100
            }

            return defaultLineHeight
        },
        getTitleStyle(asset) {
            const textColor = this.getTextColor(asset)

            return {
                fontSize: this.getFontSizeForViewport(asset.titleFontSize, 24) + 'px',
                lineHeight: this.getLineHeight(asset.titleLineHeight, 1.1),
                fontFamily: this.getFontFamily(asset.titleFont),
                color: textColor
            }
        },
        getSubtitleStyle(asset) {
            const textColor = this.getTextColor(asset)

            return {
                fontSize: this.getFontSizeForViewport(asset.subtitleFontSize, 14) + 'px',
                lineHeight: this.getLineHeight(asset.subtitleLineHeight, 1.2),
                fontFamily: this.getFontFamily(asset.subtitleFont),
                color: textColor
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;600;700;900&family=Oswald:wght@400;600;700&display=swap');

$campaign-banner-desktop-breakpoint: 1024px;

.campaign-banner-slider {
    margin: 0 0 $gap;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.campaign-banner-slider__frame {
    width: 100%;
    max-width: 700px;
    padding: 0;
    overflow: hidden;

    @media all and (min-width: $tablet) {
        max-width: 1456px;
    }

    @media all and (min-width: $campaign-banner-desktop-breakpoint) {
        max-width: 2608px;
    }
}

.campaign-slide {
    display: block;
    position: relative;
    width: 100%;
    min-height: 95px;
    height: 95px;
    overflow: hidden;
    text-decoration: none;
    color: inherit;

    @media all and (min-width: $campaign-banner-desktop-breakpoint) {
        min-height: 150px;
        height: 150px;
    }
}

// ─── Variant 2 (full-bleed image) ────────────────────────────
.campaign-slide--v2 {
    .campaign-slide__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        animation: campaign-ken-burns 16s ease-in-out infinite alternate;
    }

    .campaign-slide__image--fallback {
        display: block;
        animation: none;
    }

    .campaign-slide__overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        padding: 1rem;

        @media all and (min-width: $campaign-banner-desktop-breakpoint) {
            padding: 1.5rem;
        }
    }

    .campaign-slide__text-box {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        width: 50%;
        max-width: 50%;
        background-color: #ffffff;
        border-radius: 12px;
        padding: 0.5rem 0.75rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    }

    @media all and (min-width: $tablet) {
        .campaign-slide__text-box {
            width: 33.3333%;
            max-width: 33.3333%;
        }
    }

    @media all and (min-width: $campaign-banner-desktop-breakpoint) {
        .campaign-slide__text-box {
            padding: 1rem;
        }
    }
}

// ─── Variant 1 (color panel + square image) ─────────────────
.campaign-slide--v1 {
    .campaign-slide__v1-panel {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 33.3333%;
        display: flex;
        flex-direction: column;
        padding: 1.25rem;

        @media all and (min-width: $campaign-banner-desktop-breakpoint) {
            padding: 1.5rem;
        }
    }

    .campaign-slide__v1-image-container {
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        width: 66.6667%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem 0;

        @media all and (min-width: $campaign-banner-desktop-breakpoint) {
            padding: 1.5rem 0;
        }
    }

    .campaign-slide__text-box {
        width: 85%;
        max-width: 85%;
        background-color: transparent;
        border-radius: 0;
        box-shadow: none;
        padding: 0;
    }

    .campaign-slide__v1-square {
        max-height: 100%;
        height: 100%;
        width: auto;
        aspect-ratio: 1 / 1;
        object-fit: contain;
        animation: campaign-ken-burns 16s ease-in-out infinite alternate;
    }
}

.campaign-slide__text-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    width: 100%;
    min-height: 0;
}

.campaign-slide__title {
    font-weight: $font-weight-black;
    line-height: 1.1;
    margin: 0;
}

.campaign-slide__subtitle {
    margin: 0.5rem 0 0;
    line-height: 1.3;
    font-weight: $font-weight-semi-bold;
}

.campaign-slide__indicators-wrap {
    width: 100%;
    max-width: 700px;
    padding-top: 0.5rem;

    @media all and (min-width: $tablet) {
        max-width: 1456px;
    }

    @media all and (min-width: $campaign-banner-desktop-breakpoint) {
        max-width: 2608px;
    }
}

.campaign-slide__indicators {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    gap: 0.5rem;
}

.campaign-slide__indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 0;
    background-color: $dark-grey;
    padding: 0;
    cursor: pointer;

    &.is-active {
        background-color: $background-color-dark;
    }
}

.campaign-fade-enter-active,
.campaign-fade-leave-active {
    transition: opacity .6s ease;
}

.campaign-fade-enter-from,
.campaign-fade-leave-to {
    opacity: 0;
}

@keyframes campaign-ken-burns {
    0% {
        transform: scale(1);
    }
    100% {
        transform: scale(1.08);
    }
}
</style>
